import { periods } from '@api/api/compass-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { PriceMovementChart } from '@terminal/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { useGetInsightPredictions } from '@terminal/react-query-hooks/insights/useGetInsightPredictions';
import { getPredictionsTableData } from '@terminal/views/turbo-chart/components/insights/utils/insights-utils/insights-utils';
import { PredictionSummaryTable } from '@terminal/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';
import { useMemo } from 'react';

interface InsightChartSectionProps {
  insightId: string;
  bestHorizon?: string;
  isFixedIncome: boolean;
  snakeTs?: { index: number; value: number }[];
}

export const InsightChartSection = ({
  insightId,
  bestHorizon,
  isFixedIncome,
  snakeTs,
}: InsightChartSectionProps) => {
  const { data, isLoading } = useGetInsightPredictions(insightId);
  const predictions = data?.data;

  const predictionByHorizon = useMemo(
    () => getPredictionsTableData(periods, predictions ?? []),
    [predictions]
  );

  if (isLoading) {
    return (
      <SkeletonLoader
        data-testid="insight-chart-loading"
        areas={[
          { styles: { width: '100%', height: 312, borderRadius: '8px' } },
          { styles: { width: '100%', height: 140, borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (!predictions) {
    return <ErrorMessageWrapper variant="error" />;
  }

  return (
    <>
      <PriceMovementChart
        key={insightId}
        prediction={predictions}
        snakeTs={snakeTs}
        bestHorizon={bestHorizon}
        isFixedIncome={isFixedIncome}
        useDynamicDaysLabel
        showHorizonSelector
      />
      <PredictionSummaryTable
        data={predictionByHorizon}
        isMedian
        horizons={periods}
        isFixedIncome={isFixedIncome}
      />
    </>
  );
};
